import { ActionTree, ActionContext } from 'vuex';
import { map } from 'lodash';
import { RootState, store } from '@/store';
import { State } from './state';
import { Mutations } from './mutations';
import { KpiActionTypes } from './action-types';
import { KpiMutationTypes } from './mutation-types';
import {
  KpiTable,
  KpiTableResult,
  KpiChart,
  KpiTableAndChart,
} from '@/types/KPI';

import { KpiApiResponse, KpiGroupedApiResponse } from './types';
import ServerAPI, {
  combineQueryParams,
  handleError,
} from '@/services/serverAPI';
type AugmentedAction = {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1]
  ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<State, RootState>, 'commit'>;

export interface Actions {
  [KpiActionTypes.GET_KPI_DATA](
    { commit }: AugmentedAction,
    payload: {
      aggregateFieldType: string;
      entityId: string;
    }
  ): Promise<void>;
  [KpiActionTypes.GET_GROUPED_KPI_DATA](
    { commit }: AugmentedAction,
    payload: {
      aggregateFieldType: string;
      entityId: string;
      groupBy: string;
    }
  ): Promise<void>;
  [KpiActionTypes.CLEAR_KPI_STATE](
    { commit }: AugmentedAction,
    payload: undefined
  ): Promise<void>;
  [KpiActionTypes.GET_PROSPECT_KPI_BY_IDS](
    { commit }: AugmentedAction,
    payload: undefined
  ): Promise<void>;
}

const formatKPIResponse = (kpiData: KpiApiResponse) => {
  return {
    services: map(
      kpiData.kpis,
      item =>
        ({
          kpi: item.kpi,
          aum: item.aum,
          cash: item.cash,
          credit: item.credit,
          revenue: item.revenue,
          priceEnforcement: item.priceEnforcement,
          peExistingPortfolios: item.peExistingPortfolios,
          peNewPortfolios: item.peNewPortfolios,
          roa: item.roa,
          profitMargin: item.profitMargin,
          profit: item.profit,
          clientSpecificValues: item.clientSpecificValues,
        } as KpiTableResult)
    ),
    total: {
      aum: kpiData.totals.aum,
      cash: kpiData.totals.cash,
      credit: kpiData.totals.credit,
      revenue: kpiData.totals.revenue,
      priceEnforcement: kpiData.totals.priceEnforcement,
      peExistingPortfolios: kpiData.totals.peExistingPortfolios,
      peNewPortfolios: kpiData.totals.peNewPortfolios,
      roa: kpiData.totals.roa,
      profitMargin: kpiData.totals.profitMargin,
      profit: kpiData.totals.profit,
      clientSpecificValues: kpiData.totals.clientSpecificValues,
    } as KpiTableResult,
  };
};

export const actions: ActionTree<State, RootState> & Actions = {
  async [KpiActionTypes.GET_KPI_DATA]({ commit }, filterOptions) {
    try {
      commit(KpiMutationTypes.SET_KPI_DATA_LOADING, true);

      const paramString = combineQueryParams({
        ['filter_field']: filterOptions.aggregateFieldType,
        ['filter_id']: filterOptions.entityId,
      });
      const kpiData: KpiApiResponse = await ServerAPI.get(
        `/kpis/details${paramString}`
      );

      const tableData: KpiTable = formatKPIResponse(kpiData);

      const chartData = {
        breakdownByService: kpiData.breakdownOfAssets,
        breakdownByRevenueStatus: kpiData.breakdownByRevenueStatus,
        breakdownByProfitStatus: kpiData.breakdownByProfitStatus,
        ranking: kpiData.ranking,
        averagePe: kpiData.totals.priceEnforcement,
        status: kpiData.status,
      } as KpiChart;

      const aggregateFieldTypeName = filterOptions.aggregateFieldType;

      commit(KpiMutationTypes.SET_KPI_DATA, {
        kpiData: {
          aggregateFieldType: aggregateFieldTypeName,
          tableData,
          chartData,
        } as KpiTableAndChart,
      });
    } catch (e) {
      handleError(e);
    } finally {
      commit(KpiMutationTypes.SET_KPI_DATA_LOADING, false);
    }
  },
  async [KpiActionTypes.GET_GROUPED_KPI_DATA]({ commit }, filterOptions) {
    try {
      commit(KpiMutationTypes.SET_GROUPED_KPI_DATA_LOADING, true);

      const paramString = combineQueryParams({
        ['filter_field']: filterOptions.aggregateFieldType,
        ['filter_id']: filterOptions.entityId,
        ['group_by']: filterOptions.groupBy,
      });
      const kpiData: Array<KpiGroupedApiResponse> = await ServerAPI.get(
        `/kpis/grouped-details${paramString}`
      );

      commit(KpiMutationTypes.SET_GROUPED_KPI_DATA, kpiData);
    } catch (e) {
      handleError(e);
    } finally {
      commit(KpiMutationTypes.SET_GROUPED_KPI_DATA_LOADING, false);
    }
  },
  async [KpiActionTypes.CLEAR_KPI_STATE]({ commit }, payload) {
    commit(KpiMutationTypes.CLEAR_KPI_DATA, payload);
  },
  async [KpiActionTypes.GET_PROSPECT_KPI_BY_IDS]({ commit }) {
    try {
      const portfolios = store.state.prospectView.portfolios;
      const prospectIds = portfolios.map(p => {
        return p.scenarioId.dataValue as string;
      });

      if (prospectIds.length === 0) {
        commit(KpiMutationTypes.CLEAR_KPI_DATA, undefined);
        return;
      }

      const kpiData: KpiApiResponse = await ServerAPI.get(
        `/kpis/by-prospect-ids?prospect_ids=${prospectIds}`
      );
      const tableData: KpiTable = formatKPIResponse(kpiData);

      const chartData = {
        breakdownByService: kpiData.breakdownOfAssets,
        breakdownByProfitStatus: kpiData.breakdownByProfitStatus,
        ranking: kpiData.ranking,
        averagePe: kpiData.totals.priceEnforcement,
        status: kpiData.status,
      } as KpiChart;

      commit(KpiMutationTypes.SET_KPI_DATA, {
        kpiData: {
          aggregateFieldType: 'prospectCustomSelection',
          tableData,
          chartData,
        } as KpiTableAndChart,
      });
    } catch (e) {
      handleError(e);
    }
  },
};
